import { TabStyles } from '@tableau/widgets';
import { DataGrid, GridSelection } from '@tableau/widgets-datagrid';
import React from 'react';

import { OrderDirection } from '../../../../../gen-src/queries';
import { ExplorerStyledDataGridContainer } from '../style/DataGridStyles';
import { ListViewRowOuterHeight } from '../style/VizPortalStyles';
import {
  DataGridWithSynchronousRowStore,
  DataGridWithSynchronousRowStoreProps,
} from './DataGridWithSynchronousRowStore';
import { EmptyGridElement } from './EmptyGridElement';
import { isSelectionColumnEvent, isSelectionRowEvent } from './SelectionColumn';
import { SelectionProviderChildProps } from './SelectionProvider';
import { TypedColumn, TypedRowHandlerWithErrors, UsualDisplayGridProps } from './TypedDataGridInterfaces';
import { GridSortProps } from './sort/GridSortProps';

export enum SelectionMode {
  /** A row will be selected only when the user interacts with the Selection Cell */
  Cell,
  /** A row will be selected when the user interacts with any cell of the row */
  Row,
}

export const defaultDataGridProps: Partial<DataGridWithSynchronousRowStoreProps<unknown, unknown>> = {
  // This is to make the total left margin match the ExtraLargeSpace (24) margin in the header.
  // Our cell formatters add MediumSpace (12), so this adds another MediumSpace (12).
  leftMargin: TabStyles.Spacing.MediumSpace,
  showHeaders: true,
  showRowBanding: false,
  showHorizontalGridLines: true,
};

export type ExplorerStyledDisplayGridProps<TRowData, TError, OrderFields extends string> =
  UsualDisplayGridProps<TRowData, TError> &
  Partial<SelectionProviderChildProps> &
  GridSortProps<OrderFields> &
  {
    readonly columns: TypedColumn<TRowData, OrderFields>[];
    readonly selectionMode?: SelectionMode;
    readonly renderErrors: (errors: ReadonlyArray<TError>) => React.ReactNode;
    readonly updateSelectedRowId?: (selectedRow: number) => void;
  };

export interface ExplorerStyledDisplayGridState<TError> {
  readonly gridErrors: ReadonlyArray<TError>;
}

/**
 * A DataGrid with styling to match VizPortal Client's "Explorer" grid used for main lists.
 */
export class ExplorerStyledDisplayGrid<TRowData, TError, OrderFields extends string>
  extends React.PureComponent<ExplorerStyledDisplayGridProps<TRowData, TError, OrderFields>, ExplorerStyledDisplayGridState<TError>> {

  public constructor(props: ExplorerStyledDisplayGridProps<TRowData, TError, OrderFields>) {
    super(props);
    this.state = {
      gridErrors: [],
    };
  }

  public override render(): JSX.Element {

    const dataGridProps: DataGridWithSynchronousRowStoreProps<TRowData, TError> = {
      ...defaultDataGridProps,
      cols: this.props.columns,
      rowStore: this.props.rowStore,
      selectionModel: this.rowSelectionModel,
      onSelectionEvent: this.onSelectionEvent,
      onCellDoubleClick: this.onDoubleClickEvent,
      // The following config was lifted from the VPC/Spaces ListView.tsx
      config: {
        colsPerChunk: this.props.columns.length,
        rowOuterHeight: ListViewRowOuterHeight,
      },
      showSpinnerWhileLoading: true,
      onError: this.handleGridErrors,
      sortKey: this.props.orderBy && this.props.orderBy.field,
      sortAscending: this.props.orderBy ? (this.props.orderBy.direction === OrderDirection.ASC) : undefined,
    };

    return (
      <>
        <ExplorerStyledDataGridContainer>
          {
            this.props.rowCount === 0
              ? <EmptyGridElement emptyGridMessage={this.props.emptyGridMessage} />
              : <DataGridWithSynchronousRowStore {...dataGridProps} />
          }
        </ExplorerStyledDataGridContainer>
        {this.state.gridErrors.length > 0 ? this.props.renderErrors(this.state.gridErrors) : null}
      </>
    );
  }

  private handleGridErrors: TypedRowHandlerWithErrors<unknown, TError> = (_, gridErrors) => {
    this.setState({ gridErrors });
  }


  private onDoubleClickEvent: DataGrid.ClickHandler = (rowData: unknown, cell: DataGrid.Cell): void => {
    if (this.props.doubleClickForKey && cell.rowKey) {
      this.props.doubleClickForKey(cell.rowKey);
    }
  }

  private onSelectionEvent = (event: DataGrid.CellSelectionEvent): boolean => {
    if (this.props.updateSelectionForKey && event.cell.rowKey && this.isSelectionEvent(event)) {
      this.props.updateSelectionForKey(event.cell.rowKey);

      if (this.props.updateSelectedRowId) {
        this.props.updateSelectedRowId(event.cell.rowIdx);
      }
      return true;
    }
    // We update selection state in an ancestor component, so we don't need to tell the grid to update too
    return false;
  }

  private isSelectionEvent = (event: DataGrid.CellSelectionEvent): boolean => {
    return this.props.selectionMode && this.props.selectionMode === SelectionMode.Row ?
      isSelectionRowEvent(event) : isSelectionColumnEvent(event);
  }

  private rowSelectionModel: GridSelection = {
    isSelected: (cell: DataGrid.Cell) => {
      const selectedKeys = this.props.selectedKeys;
      return (cell.rowKey && selectedKeys) ? !!selectedKeys[cell.rowKey] : false;
    }
  };
}
